/**
 * quality_helpers.js — pure functions used by QualityScoreStep +
 * ExportDropdown. Extracted here so unit tests can hit them
 * without mounting React (jsdom overhead + snapshot brittleness).
 */

import { SECTION_KEYS } from "./utils";
import { DIMENSION_LABELS } from "./quality_copy";


// Section display titles mirror the backend
// `SECTION_DISPLAY_TITLES` verbatim. Kept in sync manually — a
// consumer test asserts the two lists match against SECTION_KEYS.
export const SECTION_DISPLAY_TITLES = {
  executive_summary: "Executive Summary",
  statement_of_need: "Statement of Need",
  organization_background: "Organization Background",
  project_description: "Project Description",
  goals_and_objectives: "Goals and Objectives",
  budget_narrative: "Budget Narrative",
  timeline: "Timeline",
  evaluation_plan: "Evaluation Plan",
  sustainability_plan: "Sustainability Plan",
  community_impact: "Community Impact",
  conclusion: "Conclusion",
};


const _CAMEL_SPLIT_RE = /(?<=[a-z0-9])(?=[A-Z])/g;


export function sectionDisplayTitle(sectionKey) {
  // Road C · C1.3 (2026-07-21) — matches the backend `section_display_title`
  // resolver line-for-line so a non-canonical key renders the same on
  // both sides of the wire (coach context payload vs. inline React).
  //
  // Resolution order:
  //   1. canonical map hit → verbatim marketing-approved title
  //   2. camelCase / kebab-case / snake_case → title-case with words
  //      split, separators normalised to spaces, whitespace collapsed
  //   3. empty / null / whitespace-only → "Section" ultimate fallback
  //      (never render a blank label)
  if (!sectionKey || !String(sectionKey).trim()) return "Section";
  const key = String(sectionKey).trim();
  const canonical = SECTION_DISPLAY_TITLES[key];
  if (canonical !== undefined) return canonical;
  const withSpaces = key
    .replace(_CAMEL_SPLIT_RE, " ")
    .replace(/_/g, " ")
    .replace(/-/g, " ");
  const normalized = withSpaces.split(/\s+/).filter(Boolean).join(" ");
  if (!normalized) return "Section";
  return normalized
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}


/**
 * Return true when the LLM dimensions (alignment + evidence)
 * should render with a neutral gray badge instead of a real score.
 *
 * Covers all G4C R5 states where LLM dims are 50-fallback:
 *   kill_switch / low_confidence / banned_phrase / schema_fail /
 *   api_error
 */
export function isLlmDimNeutral(body) {
  if (!body) return false;
  if (body.llm_disabled === true) return true;
  if (body.llm_failure_reason && body.llm_failure_reason !== null) return true;
  return false;
}


/**
 * Derive the list of weak sections from a QualityScorePublic body.
 * Returns items in ascending section_score order (weakest first).
 *
 * Server-authoritative when `body.weak_sections` is present. G4C
 * CP2 polish rerun (2026-07-20) — the backend now emits
 * `weak_sections` as the actionability bridge. When the backend
 * has spoken (the array key exists, EVEN when empty), we USE it
 * verbatim — passing through `recommended_improvement` +
 * `threshold`, both of which the frontend renders.
 *
 * `body.weak_sections === undefined` (not just falsy — check the
 * key existence) is the ONLY signal to fall back to legacy client
 * derivation. Empty array `[]` is a valid server response meaning
 * "no weak sections" — the fallback must NOT trigger. Older
 * cached responses that pre-date the field still work via the
 * legacy path.
 *
 * Fail-closed sections are OMITTED from the weak list — those are
 * already surfaced as fail-closed chips on ProposalDraft's
 * SectionCard.
 */
export function deriveWeakSections(body) {
  if (!body) return [];
  // ---- Server-authoritative path (new, preferred). ----
  if (Array.isArray(body.weak_sections)) {
    return body.weak_sections.map((entry) => ({
      sectionKey: entry.section_key,
      sectionScore: entry.score ?? 0,
      // camelCase field mirrors the frontend's existing naming
      // convention — the server derives the same list, we just
      // pass it through with a stable shape.
      weakestDim: _pickWeakestDimFromReasons(entry.reasons),
      threshold: entry.threshold ?? null,
      recommendedImprovement: entry.recommended_improvement ?? null,
      displayTitle: entry.display_title ?? null,
      reasons: Array.isArray(entry.reasons) ? entry.reasons : [],
    }));
  }
  // ---- Legacy client-derivation fallback (pre-G4C-polish payloads). ----
  if (!body.sections) return [];
  const threshold =
    body?.thresholds?.weak_section_score ?? 60;
  const items = [];
  for (const key of SECTION_KEYS) {
    const entry = body.sections[key];
    if (!entry) continue;
    if (entry.status !== "passed") continue;
    const score = entry.section_score ?? 0;
    if (score >= threshold) continue;
    items.push({
      sectionKey: key,
      sectionScore: score,
      weakestDim: _weakestDim(entry, body?.thresholds?.weak_dimension_score),
      threshold,
      // Legacy payloads have no server-derived copy — leave null
      // so the UI hides the recommendation line. `deriveWeakSections`
      // remains authoritative for whether to SHOW the section;
      // whether to show the recommendation line is a separate
      // (defensive) render-time check.
      recommendedImprovement: null,
      displayTitle: null,
      reasons: [],
    });
  }
  items.sort((a, b) => a.sectionScore - b.sectionScore);
  return items;
}


// Reason enum values are the module-level backend constants
// (see modules/proposals/quality.py::WeakSectionReason). We map
// them back to the dim key the frontend uses in DIMENSION_LABELS
// so the existing "Weakest dimension: {name}" subtext keeps
// rendering on server-driven entries.
const _REASON_TO_DIM = {
  completeness_low: "completeness",
  length_below_target: "length",
  readability_hard: "readability",
  alignment_weak: "alignment",
  evidence_thin: "evidence",
};


function _pickWeakestDimFromReasons(reasons) {
  if (!Array.isArray(reasons) || reasons.length === 0) return null;
  // Server orders `reasons` in canonical rubric order — the primary
  // (first) reason IS the one that most drives the section's
  // weakness, per _assemble_weak_sections in quality.py.
  return _REASON_TO_DIM[reasons[0]] ?? null;
}


function _weakestDim(entry, dimThreshold) {
  const dims = entry?.dimensions;
  if (!dims) return null;
  const thr = dimThreshold ?? 60;
  let worst = null;
  let worstScore = 101;
  for (const key of Object.keys(DIMENSION_LABELS)) {
    const v = dims[key];
    if (typeof v !== "number") continue;
    if (v < thr && v < worstScore) {
      worst = key;
      worstScore = v;
    }
  }
  return worst;
}


/**
 * Extract the filename from a Content-Disposition header.
 * Handles both `attachment; filename="proposal.pdf"` and
 * `attachment; filename=proposal.pdf` (unquoted). Returns null
 * if the header is missing or malformed — callers use a safe
 * fallback in that case.
 */
export function filenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  // Look for filename="..." (quoted) or filename=... (unquoted).
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (quoted && quoted[1]) return quoted[1];
  const unquoted = /filename\s*=\s*([^;]+)/i.exec(header);
  if (unquoted && unquoted[1]) return unquoted[1].trim();
  return null;
}
